import apiRequest from "./apiClient";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const signupUser = async (userData) => {
  try {
    const formData = new FormData();

    // ── Field mapping (frontend key → backend key) ──────────
    const textFields = {
      fullName: userData.fullName,
      email: userData.email,
      password: userData.password,
      mobile: userData.mobile,
      dob: userData.dob,
      birthTime: userData.birthTime,
      gender: userData.gender,
      motherTongue: userData.motherTongue,
      profileCreatedBy: userData.profileCreatedBy,
      maritalStatus: userData.maritalStatus,
      children: userData.children,
      kidsLiveWith: userData.kidsLiveWith,
      religion: userData.religion,
      caste: userData.caste,
      zodiac: userData.zodiac,
      star: userData.star,
      starPatham: userData.starPatham,
      bodyType: userData.bodyType,
      physicalStatus: userData.physicalStatus,
      height: userData.height,
      weight: userData.weight,
      complexion: userData.complexion,
      birthCountry: userData.birthCountry,
      residentCountry: userData.residentCountry,
      residentStatus: userData.residentStatus,
      city: userData.city,
      address: userData.address,
      education: userData.education,
      occupation: userData.occupation,
      fatherPlace: userData.fatherPlace,
      motherPlace: userData.motherPlace,
      fatherCaste: userData.fatherCaste,
      motherCaste: userData.motherCaste,
      fatherOccupation: userData.fatherOccupation,
      motherOccupation: userData.motherOccupation,
      hobbies: userData.hobbies,
      eating: userData.eating,
      drinking: userData.drinking,
      smoking: userData.smoking,
      kiraga: userData.kiraga,
      sevvaiLakna: userData.sevvaiLakna,
      about: userData.about,
      partner: userData.partner,
      profileStatus: userData.profileStatus,
    };

    // ── Append text fields (skip empty/null values) ──────────
    Object.entries(textFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        formData.append(key, String(value));
      }
    });

    // ── Append file fields ───────────────────────────────────
    if (userData.photo instanceof File) {
      formData.append("photo", userData.photo);
    }
    if (userData.horoscope instanceof File) {
      formData.append("horoscope", userData.horoscope);
    }

    // ── Send request (NO Content-Type header — browser sets it automatically with boundary) ──
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: "An error occurred during signup" };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: "An error occurred during signup" };
  }
};

const signinUser = async (credentials) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: 'An error occurred during signin' };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: 'An error occurred during signin' };
  }
};

const forgotPassword = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: 'An error occurred while sending OTP' };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: 'An error occurred while sending OTP' };
  }
};

const refreshAccessToken = async (refreshToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: 'An error occurred during token refresh' };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: 'An error occurred during token refresh' };
  }
};

const logoutUser = async (refreshToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: 'An error occurred during logout' };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: 'An error occurred during logout' };
  }
};

const verifyOtp = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: "OTP verification failed" };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: "OTP verification failed" };
  }
};

const resetPassword = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: "Failed to reset password" };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: "Failed to reset password" };
  }
};

const getAllUsers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiRequest(`/auth/users${query ? `?${query}` : ""}`);
};

const getSingleUser = async (userId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/auth/user/${userId}`, // ✅ FIXED HERE
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw data || { message: "Failed to fetch user details" };
    }

    return data;
  } catch (error) {
    throw error.message ? error : { message: "Failed to fetch user details" };
  }
};

const updateProfileStatus = async (id, status) => {
  const token = localStorage.getItem("accessToken"); // or redux state

  const res = await fetch(`${API_BASE_URL}/auth/user/${id}/status`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, // ✅ IMPORTANT FIX
    },
    body: JSON.stringify({ profile_status: status }),
  });

  return res.json();
};

const updateUserProfile = async (userId, userData) => {
  try {
    const token = localStorage.getItem("accessToken");

    const formData = new FormData();

    // ── Text fields mapping (frontend → backend) ──────────
    const textFields = {
      fullName: userData.fullName,
      mobile: userData.mobile,
      dob: userData.dob,
      birth_time: userData.birth_time,
      gender: userData.gender,

      mother_tongue: userData.mother_tongue,
      profile_created_by: userData.profile_created_by,
      marital_status: userData.marital_status,
      children: userData.children,
      kids_live_with: userData.kids_live_with,
      religion: userData.religion,
      caste: userData.caste,
      zodiac: userData.zodiac,
      star: userData.star,
      star_patham: userData.star_patham,

      body_type: userData.body_type,
      physical_status: userData.physical_status,
      height: userData.height,
      weight: userData.weight,
      complexion: userData.complexion,

      birth_country: userData.birth_country,
      resident_country: userData.resident_country,
      resident_status: userData.resident_status,
      city: userData.city,
      address: userData.address,

      education: userData.education,
      occupation: userData.occupation,

      father_place: userData.father_place,
      mother_place: userData.mother_place,
      father_caste: userData.father_caste,
      mother_caste: userData.mother_caste,
      father_occupation: userData.father_occupation,
      mother_occupation: userData.mother_occupation,

      hobbies: userData.hobbies,
      eating_habit: userData.eating_habit,
      drinking_habit: userData.drinking_habit,
      smoking_habit: userData.smoking_habit,

      kiraga_paavam: userData.kiraga_paavam,
      sevvai_lakna: userData.sevvai_lakna,

      about: userData.about,
      partner_qualities: userData.partner_qualities,
    };

    // ── Append text fields ──────────
    Object.entries(textFields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, String(value));
      }
    });

    // ── File uploads ──────────
    if (userData.photo instanceof File) {
      formData.append("photo", userData.photo);
    }

    if (userData.horoscope instanceof File) {
      formData.append("horoscope", userData.horoscope);
    }

    // ── PATCH request ──────────
  const response = await fetch(
  `${API_BASE_URL}/auth/user/${userId}`,
  {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData, // ✅ CORRECT
  }
);

const data = await response.json();

    if (!response.ok) {
      throw data || { message: "Failed to update profile" };
    }

    return data;
  } catch (error) {
    throw error?.message
      ? error
      : { message: "Failed to update profile" };
  }
};

const uploadVerificationDocuments = async (
  userId,
  files
) => {
  try {
    const token = localStorage.getItem("accessToken");

    const formData = new FormData();

    formData.append(
      "id_proof_front",
      files.idProofFront
    );

    formData.append(
      "id_proof_back",
      files.idProofBack
    );

    formData.append(
      "payment_receipt",
      files.paymentReceipt
    );

    const response = await fetch(
      `${API_BASE_URL}/verify/verification/${userId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

const getVerificationDetails = async (
  userId
) => {
  try {
    const token = localStorage.getItem("accessToken");

    const response = await fetch(
      `${API_BASE_URL}/verify/verification/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

export {
  signupUser, signinUser, forgotPassword, refreshAccessToken, logoutUser, verifyOtp, resetPassword, getAllUsers,
  getSingleUser, updateProfileStatus, updateUserProfile, uploadVerificationDocuments, getVerificationDetails
};
